import type { InspectionFormValues } from './inspectionReportFormDefaults';
import { defaultHvacSystem } from '../components/HvacSystemFields';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import { defaultPlumbingSystem } from '../components/PlumbingSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';

export interface ApiHvacSystem {
  id: number;
  position: number;
  location: string;
  manufacturer: string;
  age: string;
  age_condition: string;
  system_type: string | null;
  split_system_subtype: string | null;
  indoor_manufacturer: string | null;
  outdoor_manufacturer: string | null;
  overall_visual_condition: string;
  overall_operation: string;
  proper_clearance: string;
  service_record_updated: string;
  notes: string;
  boiler_ac_present: string | null;
  boiler_expansion_tank_condition: string | null;
  boiler_heat_exchanger_condition: string | null;
  boiler_pressure_relief_condition: string | null;
  boiler_auto_fill_condition: string | null;
  photo_urls: string[];
}

export interface ApiPlumbingSystem {
  id: number;
  position: number;
  location: string;
  manufacturer: string;
  age: string;
  age_condition: string;
  system_type: string | null;
  overall_visual_condition: string;
  expansion_tank_na: boolean;
  expansion_tank_condition: string;
  visible_leaks_or_rust: string;
  proper_clearance: string;
  recommended_annual_maintenance: string;
  installed_coupon: string;
  laundry_tray: string | null;
  notes: string;
  photo_urls: string[];
}

export interface ApiInspectionReport {
  id: number;
  name: string;
  technician: string;
  date: string;
  order_no: string;
  created_at: string;
  offered_maintenance_plan: string;
  electrical: Record<string, unknown>;
  water_treatment: Record<string, unknown>;
  air_quality: Record<string, unknown>;
  hvac_systems: ApiHvacSystem[];
  plumbing_systems: ApiPlumbingSystem[];
  pdf_url?: string;
}

/** Convert snake_case key to camelCase. e.g. `some_field_na` → `someFieldNa`, `n_a` → `nA` */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Coerce "true"/"false" strings (as stored in JSONB flat sections) to booleans.
 * null/undefined becomes empty string. All other values pass through.
 */
function parseValue(value: unknown): unknown {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === null || value === undefined) return '';
  return value;
}

/**
 * Maps a full API inspection report response (snake_case) to the form's camelCase state.
 *
 * Excluded from mapping:
 * - customerName, technician, date, workOrderNumber — belong to the new job context
 * - Photos — cannot copy ActiveStorage attachments
 * - id, position on systems — new report will create fresh records
 */
export function mapApiResponseToFormValues(data: ApiInspectionReport): {
  formValues: Partial<InspectionFormValues>;
  hvacSystems: HvacSystemValues[];
  plumbingSystems: PlumbingSystemValues[];
} {
  const formValues: Partial<InspectionFormValues> = {};

  if (data.offered_maintenance_plan != null) {
    formValues.offeredMaintenancePlan = data.offered_maintenance_plan;
  }

  // Flat sections: prefix the camelCase key with the section name
  const mapSection = (section: Record<string, unknown> | undefined, prefix: string) => {
    if (!section) return;
    Object.entries(section).forEach(([key, value]) => {
      const camelKey = prefix + toCamelCase(key).charAt(0).toUpperCase() + toCamelCase(key).slice(1);
      (formValues as Record<string, unknown>)[camelKey] = parseValue(value);
    });
  };

  mapSection(data.electrical, 'electrical');
  mapSection(data.water_treatment, 'waterTreatment');
  mapSection(data.air_quality, 'airQuality');

  const SKIP_SYSTEM_KEYS = new Set(['id', 'position', 'photo_urls']);

  // HVAC systems: convert keys to camelCase, drop id/position/photo_urls
  const hvacSystems: HvacSystemValues[] = (data.hvac_systems ?? []).map((sys) => {
    const mapped: Partial<HvacSystemValues> = {};
    Object.entries(sys).forEach(([key, value]) => {
      if (SKIP_SYSTEM_KEYS.has(key)) return;
      const camelKey = toCamelCase(key) as keyof HvacSystemValues;
      (mapped as Record<string, unknown>)[camelKey] = parseValue(value);
    });
    return { ...defaultHvacSystem, ...mapped };
  });

  // Plumbing systems: same pattern
  const plumbingSystems: PlumbingSystemValues[] = (data.plumbing_systems ?? []).map((sys) => {
    const mapped: Partial<PlumbingSystemValues> = {};
    Object.entries(sys).forEach(([key, value]) => {
      if (SKIP_SYSTEM_KEYS.has(key)) return;
      const camelKey = toCamelCase(key) as keyof PlumbingSystemValues;
      (mapped as Record<string, unknown>)[camelKey] = parseValue(value);
    });
    return { ...defaultPlumbingSystem, ...mapped };
  });

  return { formValues, hvacSystems, plumbingSystems };
}
